import type { FilterStates } from '@/components/Filters/useFilterStation'
import type { PermanenceInfo } from '@/types/permanence.types'
import type { CoverageInput, CoverageMode } from './CoverageRequests'
import type { MatrixMetric } from '@/Network/Types/Responses/StationResponses'

export type Observation = {
  timestamp?: string
  text: string
  author?: string
  authorId?: number
  id?: number
}

export type PositionType = {
  id: number
}

/** Payload de especialidade do plantão (spec §4.2). `newName` nasce na empresa na mesma transação. */
export type StationSpecialtyInput =
  | { officialId: number }
  | { companyId: number }
  | { newName: string }

export type StationConfig = {
  repetionDays: Record<string, string>
  positionInStationsByRepetionDays: Array<PositionType>
  positionId?: number | undefined
  /** `null` no plantão avulso sem repetição (Criar vagas da Demanda). */
  finishAtType: string | null
  occurrence?: number | null
  date: Date
  startHour: Date
  endHour: Date
  repeatIn: string[]
  finishAt: Date | undefined | null
  timezone: string
}
export type CreateStationRequest = CoverageInput & {
  scheduleId: number
  healthUnitId: number
  doctorId: number | undefined
  shift: number
  formation?: number | undefined
  userSponsorId: number | undefined // Old: sponsorDoctorId
  stationType: string
  ourTeam: boolean
  value: number | undefined
  config: StationConfig
  inCash: boolean
  advanceRequested: boolean
  advancePaid: boolean
  observations: Observation[]
  /** Opcional. Ausente = sem setor. Vale para todos os plantões gerados pela repetição. */
  sectorId?: number | null
  specialty?: StationSpecialtyInput | null
}

export enum DashboardType {
  MONTHLY = 'MONTHLY',
  WEEK = 'WEEK',
  /** Intervalo livre; usado pelo painel do dia do Calendário (1 dia). */
  GENERAL = 'GENERAL'
}
export type ListFiltersRequest = {
  type: DashboardType
  startDate: string
  endDate: string
  startHour?: string
  endHour?: string
  schedules?: string[]
  healthUnits?: string[]
  doctorPending?: string
  limit?: string
  offset?: string

  doctorIds?: string[] | string
  openedStation?: string
  stationType?: string[]
  valueMin?: string
  valueMax?: string
  paymentValueMin?: string
  paymentValueMax?: string
  withCheckin?: string
  withCheckout?: string
  inCash?: string
  advanceRequested?: string
  advancePaid?: string
  ourTeam?: string
  status?: string
  operationalGroups?: string[]
  sectorIds?: string[]
  specialtyIds?: string[]
  companySpecialtyIds?: string[]
  withoutSector?: string
  withoutSpecialty?: string
  /** Paginação por escala (Mês): tamanho do lote e deslocamento. */
  schedulesLimit?: string
  schedulesOffset?: string
  coverageStatus?: string // CSV: OPEN,DOCTOR,UNIDENTIFIED
}

export type ConsultingRequest = {
  columnFilters?: FilterStates
  perPage: number
  page: number
  sort: {
    field: string
    type: 'asc' | 'desc'
  }[]
}

export type StationTypes = 'FIXO' | 'COBERTURA'

export type DutyFee = {
  formation: string
  value: number
  repassMode: string
}

export type UpdateStationRequest = CoverageInput & {
  doctorId?: number
  formation?: number
  position: string | number
  team?: string
  userSponsorId?: number | null // Old: sponsorDoctorId
  stationType: StationTypes
  ourTeam: boolean
  inCash: boolean
  value?: number
  justification?: string
  config: {
    date: Date
    startHour: Date
    endHour: Date
    timezone: string
  }
  histories: HistoryDateRequest[]
  advanceRequested: boolean
  advancePaid: boolean
  observations: Observation[]
  modifiedValue: boolean
  dutyFee?: DutyFee
  scheduleId?: number
  /** Semântica de patch: ausente mantém, `null` limpa. */
  sectorId?: number | null
  specialty?: StationSpecialtyInput | null
}

export type PatchStationsRequest = CoverageInput & {
  schedule?: number
  healthUnit?: number
  company?: number
  doctorId?: number | null
  userSponsorId?: number | null // Old: sponsorDoctorId
  dsPaymentDoctorFormationId?: number | null
  stationType?: StationTypes | null
  ourTeam?: boolean
  value?: number
  justification?: string
  observations?: string
  inCash?: boolean
  modifiedValue?: boolean
  advancePaid?: boolean
  advanceRequested?: boolean
  config?: {
    date?: string
    startHour?: string
    endHour?: string
    timezone?: string
  }
  histories?: HistoryDateRequest[]
}

export enum CheckOrigin {
  DOCTOR = 'DOCTOR',
  WEB = 'WEB',
  FOLHA = 'FOLHA',
  EXTERNAL_APP = 'EXTERNAL_APP'
}

export interface CheckEvidence {
  id?: number
  isEditing?: boolean
  newName?: string
  name: string
  path: string
  type?: string
  eventType?: string | null
  size?: number
  uploadedBy?: { id: number; name: string } | null
}

export interface HistoryDate {
  id?: number
  checkin: string
  labelCheckin?: string
  checkinLatitude?: string | number
  checkinLongitude?: string | number
  checkout?: string
  checkoutLatitude?: string | number
  checkoutLongitude?: string | number
  labelCheckout?: string
  origin: CheckOrigin
  isMain: boolean
  checkEvidences?: CheckEvidence[]
  evidence?: File[]
  permanence?: PermanenceInfo | null
  /**
   * Container de Registro de Atividade que originou este check (B17 §1.2): cada item de
   * `doctorStation.doctorChecks[]` do `GET /stations/:id` traz `number` ou `null`.
   * Opcional aqui porque o mesmo shape é montado no cliente (rascunho de check novo) e
   * por backends anteriores à F2 — quando o campo não vem, o link "ver registro #id"
   * simplesmente não aparece.
   */
  dsAttendanceContainerId?: number | null
}

export interface HistoryDateRequest {
  id?: number
  checkin: Date
  checkout?: Date
  origin: CheckOrigin
  /**
   * @deprecated F2/BF5 — o servidor decide `is_main` em `ensureMainCheck` e
   * ignora o que o cliente enviar (DTO marcado `@deprecated` no backend).
   * Opcional só porque este tipo também descreve os `checks` de LEITURA em
   * `StationWeekList`; `getHistoriesRequest` nunca mais o povoa no request.
   */
  isMain?: boolean
  checkEvidences?: CheckEvidence[]
  // Read-only: só vem na resposta, o FE nunca envia esse bloco no request —
  // está aqui por causa do shape compartilhado com StationWeekList.checks.
  permanence?: PermanenceInfo | null
}

export type RelatoryMonthlyExpensesFilter = {
  healthUnits?: number[]
  month?: number
}

export type ApproveStationRequest = {
  stationId: number
  value: number
}

export type ListStationsFromScheduleRequest = {
  schedule: number
  doctor?: number
  position?: number | null
  startDate?: string
  endDate?: string
  weekDays?: string
  weekOfMonth?: string
  shifts?: string
  station?: number
  limit?: number
  offset?: number
  doctorStationPackage?: number
  alternatingWeek?: boolean
  stationWeekParity?: 0 | 1
  excludeHasCheckIn?: boolean
  coverage?: CoverageMode
}

export enum DoctorStationPackageAuditType {
  CREATE = 'CREATE',
  TRANSFER = 'TRANSFER',
  REMOVE = 'REMOVE',
  UNIDENTIFIED = 'UNIDENTIFIED'
}

export interface PatchStationsFromSchedulePayload {
  operations: Array<{
    packageId?: number | null
    doctorStationIds?: number[]
    stationIds: number[]
    doctorId: number | null
    reason: DoctorStationPackageAuditType
  }>
  patchStations: PatchStationsRequest
  scheduleId: number
}

// Candidato de alocação enviado ao preview POST /stations/consecutive-hours/check.
// `startHour`/`endHour` são instantes ISO; `stationId` só existe em edição.
export interface ConsecutiveHoursCheckCandidate {
  scheduleId: number
  doctorId: number
  startHour: string
  endHour: string
  ourTeam: boolean
  timezone: string
  stationId?: number
  excludeDoctorStationIds?: number[]
}

export type BulkFieldMode = 'keep' | 'set' | 'clear'

/**
 * `PATCH /stations/bulk/move`: move plantões (station ids, 1..500) para outra escala numa
 * request só, mesmo com várias escalas de origem. Só o plantão muda — médico e pacote ficam.
 */
export type MoveStationsRequest = { ids: number[]; targetScheduleId: number }

/** `PATCH /stations/bulk/sector-specialty` (spec §4.2). */
export type BulkSectorSpecialtyRequest = {
  ids: number[]
  sector: { mode: BulkFieldMode; id?: number }
  specialty: { mode: BulkFieldMode; value?: StationSpecialtyInput }
  /** `true` = prévia: o BE desfaz a transação e só devolve os contadores (DECISIONS A8). */
  dryRun?: boolean
}

/** Mesmos filtros do `GET /stations`, sem tipo nem paginação. */
export type CalendarSummaryRequest = Omit<
  ListFiltersRequest,
  'type' | 'limit' | 'offset' | 'schedulesLimit' | 'schedulesOffset'
>

/** Porta de entrada da telemetria do web: o aviso do médico coringa abriu ANTES de enviar. */
export type PlaceholderAllocationEntryPoint = 'WEB_PREVENTIVE'

/** `POST /stations/placeholder-allocation-attempts` — só no bloqueio preventivo (nunca no 422). */
export type PlaceholderAllocationAttemptRequest = {
  doctorId: number
  scheduleId: number
  stationIds?: number[]
  entryPoint: PlaceholderAllocationEntryPoint
}

/** `GET /stations/matrix-summary` (Onda 2.1, spec §3): filtros do calendário + métrica. */
export type MatrixSummaryRequest = CalendarSummaryRequest & { metric?: MatrixMetric }
